// Typed client for the SalonFlow API. All errors become ApiError with the server's Thai message.
import type {
  Booking, CustomerBooking, CustomerRow, CustomerSelf, DateAvailability, Me, PublicShop, Quota, Report, Resolution, Schedule,
  MenuExtras, Settings, ShopAdmin, StaffBooking, StaffCtx, StaffMember, TimeSlot,
} from './types.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;
  readonly details?: Record<string, unknown>;
  constructor(status: number, code: string, message: string, field?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.field = field;
    this.details = details;
  }
}

export interface ApiOptions {
  baseUrl: string;
  slug: string;
  getToken: () => string | null;
  onAuthError?: (code: string) => void;
  fetchImpl?: typeof fetch;
}

type Query = Record<string, string | null | undefined>;
type Resolutions = Record<string, Resolution> | undefined;

export function createApi(o: ApiOptions) {
  const doFetch = o.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = `${o.baseUrl.replace(/\/+$/, '')}/api/s/${encodeURIComponent(o.slug)}`;
  const enc = encodeURIComponent;

  async function call<T>(method: string, path: string, opts: { query?: Query; body?: unknown; auth?: boolean } = {}): Promise<T> {
    let url = base + path;
    if (opts.query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(opts.query)) if (v !== null && v !== undefined && v !== '') qs.set(k, v);
      const s = qs.toString();
      if (s) url += `?${s}`;
    }
    const headers: Record<string, string> = {};
    if (opts.auth !== false) {
      const token = o.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    let res: Response;
    try {
      res = await doFetch(url, { method, headers, body: opts.body === undefined ? undefined : JSON.stringify(opts.body) });
    } catch {
      throw new ApiError(0, 'NETWORK', 'เชื่อมต่อไม่ได้ กรุณาลองใหม่');
    }
    const text = await res.text();
    let data: unknown = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = null; }
    if (!res.ok) {
      const err = (data as { error?: { code?: string; message?: string; field?: string; details?: Record<string, unknown> } } | null)?.error;
      const e = err?.code
        ? new ApiError(res.status, err.code, err.message ?? 'ระบบขัดข้อง กรุณาลองใหม่', err.field, err.details)
        : new ApiError(res.status, 'INTERNAL', 'ระบบขัดข้อง กรุณาลองใหม่');
      if (res.status === 401) o.onAuthError?.(e.code);
      throw e;
    }
    return data as T;
  }

  return {
    shop: () => call<PublicShop>('GET', '/shop', { auth: false }),
    /** Address of the shop's logo picture (the version makes a changed logo load fresh). */
    logoUrl: (version: string) => `${base}/logo?v=${enc(version)}`,
    me: () => call<Me>('GET', '/me'),
    register: (b: { fullName: string; phone: string; consent: boolean }) => call<CustomerSelf>('POST', '/register', { body: b }),
    updateProfile: (b: { fullName: string; phone: string }) => call<CustomerSelf>('PUT', '/profile', { body: b }),
    deleteMe: () => call<{ ok: true }>('POST', '/delete-me', { body: {} }),
    availableDates: (q: { serviceId: string; barberId: string | null }) =>
      call<DateAvailability[]>('GET', '/availability/dates', { query: { serviceId: q.serviceId, barberId: q.barberId } }),
    availableTimes: (q: { serviceId: string; barberId: string | null; date: string; mode: 'customer' | 'staff' }) =>
      call<TimeSlot[]>('GET', '/availability/times', { query: { serviceId: q.serviceId, barberId: q.barberId, date: q.date, mode: q.mode } }),
    myBookings: () => call<{ upcoming: CustomerBooking[]; past: CustomerBooking[] }>('GET', '/bookings'),
    createBooking: (b: { serviceId: string; barberId: string | null; startAt: string; note?: string; idempotencyKey: string }) =>
      call<Booking>('POST', '/bookings', { body: b }),
    cancelBooking: (id: string, reason?: string) => call<Booking>('POST', `/bookings/${enc(id)}/cancel`, { body: { reason } }),
    reschedule: (id: string, b: { barberId: string | null; startAt: string; idempotencyKey: string }) =>
      call<Booking>('POST', `/bookings/${enc(id)}/reschedule`, { body: b }),
    acceptInvite: (token: string) => call<StaffCtx>('POST', '/invites/accept', { body: { token } }),

    staff: {
      bookings: (date: string, barberId?: string) => call<StaffBooking[]>('GET', '/staff/bookings', { query: { date, barberId } }),
      createBooking: (b: {
        customerId: string | null; guestName?: string; walkIn?: boolean; barberId: string; serviceId: string; startAt: string;
        note?: string; idempotencyKey: string; confirmOutsideHours?: boolean; confirmTimeOff?: boolean;
      }) => call<Booking>('POST', '/staff/bookings', { body: b }),
      bookingAction: (id: string, action: 'complete' | 'no-show' | 'undo' | 'cancel' | 'move', b: Record<string, unknown> & { version: number }) =>
        call<Booking>('POST', `/staff/bookings/${enc(id)}/${action}`, { body: b }),
      customers: (q: string) => call<CustomerRow[]>('GET', '/staff/customers', { query: { q } }),
      createCustomer: (b: { fullName: string; phone?: string }) => call<CustomerRow>('POST', '/staff/customers', { body: b }),
      matches: () => call<{ lineCustomer: CustomerRow; phoneCustomer: CustomerRow }[]>('GET', '/staff/customers/matches'),
      merge: (sourceId: string, targetId: string) => call<CustomerRow>('POST', '/staff/customers/merge', { body: { sourceId, targetId } }),
      updateCustomer: (id: string, patch: { staffNote?: string | null; block?: boolean }) =>
        call<CustomerRow>('PATCH', `/staff/customers/${enc(id)}`, { body: patch }),
      customerHistory: (id: string) => call<StaffBooking[]>('GET', `/staff/customers/${enc(id)}/bookings`),
      schedule: () => call<Schedule>('GET', '/staff/schedule'),
      upsertBarber: (b: { id?: string; name: string; photoUrl?: string | null; sortOrder?: number; serviceIds: string[]; active?: boolean; resolutions?: Resolutions }) =>
        call<{ barberId: string }>('PUT', '/staff/barbers', { body: b }),
      upsertService: (b: { id?: string; name: string; durationMin: number; price: number; active?: boolean; sortOrder?: number; barberIds: string[]; resolutions?: Resolutions }) =>
        call<{ serviceId: string }>('PUT', '/staff/services', { body: b }),
      setHours: (barberId: string, ranges: { weekday: number; startMin: number; endMin: number }[], resolutions?: Resolutions) =>
        call<unknown>('PUT', `/staff/barbers/${enc(barberId)}/hours`, { body: { ranges, resolutions } }),
      invite: (barberId: string) => call<{ url: string | null; expiresAt: string }>('POST', `/staff/barbers/${enc(barberId)}/invite`, { body: {} }),
      addSpecialHours: (b: { barberId: string; date: string; startMin: number; endMin: number }) =>
        call<{ id: string }>('POST', '/staff/special-hours', { body: b }),
      deleteSpecialHours: (id: string, resolutions?: Resolutions) =>
        call<unknown>('POST', `/staff/special-hours/${enc(id)}/delete`, { body: { resolutions } }),
      addTimeOff: (b: { barberId: string | null; startAt: string; endAt: string; reason?: string; resolutions?: Resolutions }) =>
        call<{ id: string }>('POST', '/staff/time-off', { body: b }),
      deleteTimeOff: (id: string) => call<unknown>('DELETE', `/staff/time-off/${enc(id)}`),
      staffList: () => call<StaffMember[]>('GET', '/staff/staff'),
      removeStaff: (lineUserId: string) => call<unknown>('DELETE', `/staff/staff/${enc(lineUserId)}`),
      settings: () => call<ShopAdmin>('GET', '/staff/settings'),
      updateSettings: (patch: Partial<Settings>) => call<Settings>('PATCH', '/staff/settings', { body: patch }),
      updateShopInfo: (patch: { name?: string; phone?: string; address?: string; mapUrl?: string; privacyNotice?: string }) =>
        call<unknown>('PATCH', '/staff/shop-info', { body: patch }),
      groupLinkCode: () => call<{ code: string; expiresAt: string }>('POST', '/staff/group-link-code', { body: {} }),
      quota: () => call<Quota>('GET', '/staff/quota'),
      report: (from: string, to: string) => call<Report>('GET', '/staff/reports', { query: { from, to } }),
      exportBookings: (from: string, to: string) => call<Record<string, string | number>[]>('GET', '/staff/export/bookings', { query: { from, to } }),
      exportCustomers: () => call<Record<string, string | number>[]>('GET', '/staff/export/customers'),
      richMenuImage: (imageBase64: string, contentType: string, extras?: MenuExtras) =>
        call<unknown>('POST', '/staff/rich-menu-image', { body: { imageBase64, contentType, ...extras } }),
    },
  };
}

export type Api = ReturnType<typeof createApi>;
